/* eslint-disable @ngrx/prefix-selectors-with-select */
import { createSelector } from '@ngrx/store';

import { selectCompareState } from '@cdba/core/store/reducers';
import {
  CostComponentSplitItem,
  CostComponentSplitType,
  RawMaterialAnalysis,
  SchaefflerBomIdentifier,
  SchaefflerBomItem,
} from '@cdba/shared/models';
import {
  aggregateRawMaterials,
  calculateCostAndPrice,
  calculateRawMaterialsSummary,
  extractRawMaterials,
} from '@cdba/shared/utils';

import { CompareState } from '../..';

export const getBomItems = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState) => state[props.index]?.billOfMaterial?.items
  );

export const getBomLoading = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState) => state[props.index]?.billOfMaterial?.loading
  );

export const getBomError = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState) => state[props.index]?.billOfMaterial?.errorMessage
  );

export const selectSelectedItem = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState): SchaefflerBomItem => {
      const selectedItemRowId =
        state[props.index]?.billOfMaterial?.selectedItemRowId;

      if (!selectedItemRowId) {
        return undefined;
      }

      return state[props.index]?.billOfMaterial?.items.find(
        (item) => item.rowId === selectedItemRowId
      );
    }
  );

export const getDirectChildrenOfSelectedBomItem = (props: { index: number }) =>
  createSelector(
    selectSelectedItem(props),
    selectCompareState,
    (
      selectedItem: SchaefflerBomItem,
      state: CompareState
    ): SchaefflerBomItem[] =>
      selectedItem
        ? state[props.index]?.billOfMaterial?.items.filter(
            (item) =>
              item.path.at(-2) === selectedItem.rowId &&
              item.level === selectedItem.level + 1
          )
        : undefined
  );

export const selectSelectedBomItemRowId = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState): string =>
      state[props.index]?.billOfMaterial?.selectedItemRowId
  );

export const getComparingBoms = createSelector(
  selectCompareState,
  (state: CompareState) => [state[0]?.billOfMaterial, state[1]?.billOfMaterial]
);

export const areBomIdentifiersForSelectedBomItemsLoaded = createSelector(
  selectSelectedItem({ index: 0 }),
  selectSelectedItem({ index: 1 }),
  (
    firstSelectedItem: SchaefflerBomItem,
    secondSelectedItem: SchaefflerBomItem
  ): boolean =>
    firstSelectedItem?.bomIdentifier !== undefined &&
    secondSelectedItem?.bomIdentifier !== undefined
);

export const getBomIdentifiersForSelectedBomItems = createSelector(
  selectSelectedItem({ index: 0 }),
  selectSelectedItem({ index: 1 }),
  (
    firstSelectedItem: SchaefflerBomItem,
    secondSelectedItem: SchaefflerBomItem
  ): SchaefflerBomIdentifier[] => [
    firstSelectedItem.bomIdentifier,
    secondSelectedItem.bomIdentifier,
  ]
);

export const areBomItemsValidForComparison = createSelector(
  selectCompareState,
  (state: CompareState): boolean =>
    [0, 1].every(
      (i) =>
        !state[i].billOfMaterial?.loading &&
        !!state[i].billOfMaterial?.items?.length
    )
);

export const getBomIdentifierForSelectedBomItem = (props: { index: number }) =>
  createSelector(
    selectSelectedItem(props),
    (selectedBomItem: SchaefflerBomItem): SchaefflerBomIdentifier =>
      selectedBomItem?.bomIdentifier
  );

export const getAllChildrenOfSelectedBomItem = (props: { index: number }) =>
  createSelector(
    selectSelectedItem(props),
    selectCompareState,
    (
      selectedBomItem: SchaefflerBomItem,
      state: CompareState
    ): SchaefflerBomItem[] => {
      if (state[props.index]?.billOfMaterial?.items && selectedBomItem) {
        const allBomItems = state[props.index].billOfMaterial.items;
        const allChildren: SchaefflerBomItem[] = [];

        for (
          let i = allBomItems.indexOf(selectedBomItem) + 1;
          i < allBomItems.length;
          i += 1
        ) {
          if (allBomItems[i].level <= selectedBomItem.level) {
            break;
          } else {
            allChildren.push(allBomItems[i]);
          }
        }

        return allChildren;
      } else {
        return undefined;
      }
    }
  );

export const getRawMaterialAnalysisForSelectedBomItem = (props: {
  index: number;
}) =>
  createSelector(
    selectSelectedItem(props),
    getAllChildrenOfSelectedBomItem(props),
    (
      selectedBomItem: SchaefflerBomItem,
      childrenOfSelectedBomItem: SchaefflerBomItem[]
    ): RawMaterialAnalysis[] => {
      if (selectedBomItem && childrenOfSelectedBomItem) {
        const rawMaterialBomItems: SchaefflerBomItem[] = extractRawMaterials(
          childrenOfSelectedBomItem
        );

        if (rawMaterialBomItems) {
          const aggregatedRawMaterialAnalyses: RawMaterialAnalysis[] =
            aggregateRawMaterials(rawMaterialBomItems);

          return calculateCostAndPrice(
            aggregatedRawMaterialAnalyses,
            selectedBomItem.costing.costAreaTotalValue
          );
        } else {
          return undefined;
        }
      } else {
        return undefined;
      }
    }
  );

export const getRawMaterialAnalysisSummaryForSelectedBom = (props: {
  index: number;
}) =>
  createSelector(
    getRawMaterialAnalysisForSelectedBomItem(props),
    (rawMaterialAnalysisData: RawMaterialAnalysis[]): RawMaterialAnalysis[] =>
      calculateRawMaterialsSummary(rawMaterialAnalysisData)
  );

export const getCostComponentSplitItems = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState): CostComponentSplitItem[] =>
      state[props.index]?.costComponentSplit?.items
        ? state[props.index].costComponentSplit.items.filter(
            (item) =>
              item.splitType ===
              state[props.index].costComponentSplit.selectedSplitType
          )
        : undefined
  );

export const getCostComponentSplitLoading = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState) => state[props.index]?.costComponentSplit?.loading
  );

export const getCostComponentSplitError = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState) =>
      state[props.index]?.costComponentSplit?.errorMessage
  );

export const getCostComponentSplitSummary = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState): CostComponentSplitItem[] =>
      state[props.index]?.costComponentSplit?.items?.filter(
        (item) => item.splitType === CostComponentSplitType.TOTAL
      )
  );

export const getSelectedSplitType = (props: { index: number }) =>
  createSelector(
    selectCompareState,
    (state: CompareState): CostComponentSplitType =>
      state[props.index]?.costComponentSplit?.selectedSplitType
  );
